import type { AbilityDefinition, ContinentId, CreatureDefinition, StyleId } from './types';

/**
 * The creature that is used when a requested id cannot be found. This only
 * happens with stale saved data or a typo, and `getCreature` reports it loudly;
 * it exists so a single bad id shows a placeholder instead of breaking a battle.
 */
export const FALLBACK_SPECIES_ID = 'africa-kibiko';

type Rarity = CreatureDefinition['rarity'];

type MoveName = 'Pulse Pebble' | 'Bramble Rush' | 'Glimmer Field' | 'Softshell' | 'Comet Seed' | 'Rattleclap' | 'BoomBox' | 'Dash Peck' | 'Spitcorn';

interface MoveDefinition {
  name: MoveName;
  kind: AbilityDefinition['kind'];
  delivery: AbilityDefinition['delivery'];
  effect: AbilityDefinition['effect'];
  range: number;
  radius: number;
  projectileSpeed: number;
  cost: number;
  cooldown: number;
  windup: number;
  recovery: number;
  requiresLineOfSight: boolean;
}

/**
 * The shared move pool. A move describes mechanics only; each creature supplies
 * its own tuned power and colour for the moves it uses.
 */
const movePool: MoveDefinition[] = [
  { name: 'Pulse Pebble', kind: 'projectile', delivery: 'projectile', effect: 'damage', range: 190, radius: 10, projectileSpeed: 235, cost: 24, cooldown: 4.1, windup: 0.48, recovery: 0.34, requiresLineOfSight: true },
  { name: 'Bramble Rush', kind: 'melee', delivery: 'rush', effect: 'damage', range: 78, radius: 21, projectileSpeed: 0, cost: 18, cooldown: 3.6, windup: 0.28, recovery: 0.48, requiresLineOfSight: true },
  { name: 'Glimmer Field', kind: 'area', delivery: 'burst', effect: 'damage', range: 130, radius: 48, projectileSpeed: 0, cost: 34, cooldown: 6.2, windup: 0.72, recovery: 0.56, requiresLineOfSight: false },
  { name: 'Softshell', kind: 'support', delivery: 'self', effect: 'guard', range: 0, radius: 0, projectileSpeed: 0, cost: 30, cooldown: 8.4, windup: 0.35, recovery: 0.4, requiresLineOfSight: false },
  { name: 'Comet Seed', kind: 'projectile', delivery: 'projectile', effect: 'damage', range: 225, radius: 13, projectileSpeed: 175, cost: 32, cooldown: 5.2, windup: 0.62, recovery: 0.46, requiresLineOfSight: true },
  { name: 'Rattleclap', kind: 'area', delivery: 'cone', effect: 'damage', range: 105, radius: 34, projectileSpeed: 0, cost: 26, cooldown: 5.7, windup: 0.54, recovery: 0.42, requiresLineOfSight: true },
  { name: 'BoomBox', kind: 'area', delivery: 'cone', effect: 'damage', range: 105, radius: 34, projectileSpeed: 0, cost: 26, cooldown: 5.7, windup: 0.54, recovery: 0.42, requiresLineOfSight: true },
  // Cluckoo's signature peck. Mechanics identical to Bramble Rush; the separate
  // entry is what lets this one creature name the move for itself, exactly as
  // BoomBox does for Kameboom. The ten creatures using Bramble Rush are untouched.
  { name: 'Dash Peck', kind: 'melee', delivery: 'rush', effect: 'damage', range: 78, radius: 21, projectileSpeed: 0, cost: 18, cooldown: 3.6, windup: 0.28, recovery: 0.48, requiresLineOfSight: true },
  // Cluckoo's ranged option. Mechanics identical to Pulse Pebble.
  { name: 'Spitcorn', kind: 'projectile', delivery: 'projectile', effect: 'damage', range: 190, radius: 10, projectileSpeed: 235, cost: 24, cooldown: 4.1, windup: 0.48, recovery: 0.34, requiresLineOfSight: true },
];

const movesByName = Object.fromEntries(movePool.map((move) => [move.name, move])) as Record<MoveName, MoveDefinition>;

/** Region, habitat and element are properties of the continent, so they are defined once here. */
const regions: Record<ContinentId, { region: string; habitat: string; element: string }> = {
  africa: { region: 'Sunfall Savanna', habitat: 'grassland', element: 'sun' },
  asia: { region: 'Jade Canopy', habitat: 'forest', element: 'leaf' },
  europe: { region: 'Mistral Vale', habitat: 'meadow', element: 'wind' },
  'north-america': { region: 'Copperpine Range', habitat: 'mountain', element: 'stone' },
  'south-america': { region: 'Verdant Basin', habitat: 'rainforest', element: 'water' },
  oceania: { region: 'Glassreef Coast', habitat: 'coast', element: 'tide' },
};

/**
 * How hard a captured creature of each rarity is to catch.
 *
 * Rarity is read here as well as in the encounter, so a rarer creature is both
 * harder to meet and harder to hold. That is deliberate: the ladder is the
 * game's difficulty ladder, and it would be odd for a legendary to be the
 * hardest to find and then as easy to catch as a common.
 */
const catchDifficultyByRarity: Record<Rarity, number> = {
  common: 0.86,
  uncommon: 1.05,
  rare: 1.22,
  'super-rare': 1.45,
  legendary: 1.75,
};

export const continentNames: Record<ContinentId, string> = {
  africa: 'Africa', asia: 'Asia', europe: 'Europe', 'north-america': 'North America', 'south-america': 'South America', oceania: 'Oceania',
};

interface AbilitySeed {
  /** Move from the shared pool that supplies the mechanics and the ability name. */
  move: MoveName;
  /** This creature's own version of the move, after per-creature tuning. */
  power: number;
  /** Ability colour, chosen per creature. */
  color: number;
}

interface SpeciesSeed {
  id: string;
  name: string;
  continent: ContinentId;
  rarity: Rarity;
  style: StyleId;
  stats: { hp: number; power: number; defence: number; speed: number };
  /**
   * The creature's body colour, and its accent: the colour of the markings on its
   * orb and card, and of the eyes in the placeholder art.
   *
   * The placeholder art is drawn in these two tones and nothing else, so two
   * creatures that share both are drawn as each other. Every creature must
   * therefore differ from every other in at least one of the two.
   */
  color: number;
  accent: number;
  abilityA: AbilitySeed;
  abilityB: AbilitySeed;
}

/**
 * Every creature in the game, written out one at a time.
 *
 * This is content rather than a formula: a creature's stats, style and moves are
 * exactly what its entry says, and nothing is derived from its position in the
 * list. Adding or reordering a creature can therefore never change another one.
 *
 * The `scripts/check-roster.sh` fixture proves that property after every edit.
 */
const speciesSeeds: SpeciesSeed[] = [
  {
    id: 'africa-kibiko', name: 'Kibiko', continent: 'africa',
    rarity: 'common', style: 'hunter',
    stats: { hp: 131, power: 16, defence: 14, speed: 55 },
    color: 0x56c48b, accent: 0xe8d47e,
    abilityA: { move: 'Pulse Pebble', power: 17, color: 0x56c48b },
    abilityB: { move: 'Glimmer Field', power: 14, color: 0xefa947 },
  },
  {
    id: 'africa-tumbara', name: 'Tumbara', continent: 'africa',
    rarity: 'common', style: 'skirmisher',
    stats: { hp: 148, power: 18, defence: 16, speed: 60 },
    color: 0x7e9de5, accent: 0xf5dcad,
    abilityA: { move: 'Bramble Rush', power: 24, color: 0xefa947 },
    abilityB: { move: 'Softshell', power: 0, color: 0x7e9de5 },
  },
  {
    id: 'africa-nimblet', name: 'Nimblet', continent: 'africa',
    rarity: 'common', style: 'guardian',
    stats: { hp: 166, power: 21, defence: 11, speed: 65 },
    color: 0x70c8cc, accent: 0xf18c72,
    abilityA: { move: 'Glimmer Field', power: 15, color: 0x7e9de5 },
    abilityB: { move: 'Comet Seed', power: 22, color: 0xd77bc2 },
  },
  {
    id: 'africa-baobloom', name: 'Baobloom', continent: 'africa',
    rarity: 'common', style: 'trickster',
    stats: { hp: 146, power: 24, defence: 13, speed: 70 },
    color: 0xb9d95b, accent: 0xe5e6c6,
    abilityA: { move: 'Softshell', power: 0, color: 0xd77bc2 },
    abilityB: { move: 'Rattleclap', power: 17, color: 0x70c8cc },
  },
  {
    id: 'africa-sundilo', name: 'Sundilo', continent: 'africa',
    rarity: 'common', style: 'ambusher',
    stats: { hp: 164, power: 18, defence: 16, speed: 55 },
    color: 0x54aebb, accent: 0xf0a8c3,
    abilityA: { move: 'Comet Seed', power: 23, color: 0x70c8cc },
    abilityB: { move: 'Pulse Pebble', power: 20, color: 0xdb7767 },
  },
  {
    id: 'asia-mizumi', name: 'Mizumi', continent: 'asia',
    rarity: 'common', style: 'skirmisher',
    stats: { hp: 132, power: 16, defence: 14, speed: 60 },
    color: 0xefa947, accent: 0xf18c72,
    abilityA: { move: 'Bramble Rush', power: 23, color: 0xefa947 },
    abilityB: { move: 'Softshell', power: 0, color: 0x7e9de5 },
  },
  {
    id: 'asia-pandanox', name: 'Pandanox', continent: 'asia',
    rarity: 'common', style: 'guardian',
    stats: { hp: 150, power: 18, defence: 16, speed: 65 },
    color: 0xd77bc2, accent: 0xe5e6c6,
    abilityA: { move: 'Glimmer Field', power: 14, color: 0x7e9de5 },
    abilityB: { move: 'Comet Seed', power: 21, color: 0xd77bc2 },
  },
  {
    id: 'asia-kameboom', name: 'Kameboom', continent: 'asia',
    rarity: 'common', style: 'brawler',
    stats: { hp: 168, power: 17, defence: 17, speed: 65 },
    color: 0xdb7767, accent: 0xf0a8c3,
    abilityA: { move: 'Softshell', power: 0, color: 0xd77bc2 },
    abilityB: { move: 'BoomBox', power: 17, color: 0x70c8cc },
  },
  {
    id: 'asia-pebblin', name: 'Pebblin', continent: 'asia',
    rarity: 'common', style: 'ambusher',
    stats: { hp: 147, power: 24, defence: 13, speed: 55 },
    color: 0x9c80d9, accent: 0x8cd5b5,
    abilityA: { move: 'Comet Seed', power: 23, color: 0x70c8cc },
    abilityB: { move: 'Pulse Pebble', power: 20, color: 0xdb7767 },
  },
  {
    id: 'asia-lotuff', name: 'Lotuff', continent: 'asia',
    rarity: 'common', style: 'survivor',
    stats: { hp: 166, power: 18, defence: 16, speed: 60 },
    color: 0xec8c53, accent: 0xf6ca58,
    abilityA: { move: 'Rattleclap', power: 18, color: 0xdb7767 },
    abilityB: { move: 'Bramble Rush', power: 27, color: 0xb9d95b },
  },
  {
    id: 'europe-brindlew', name: 'Brindlew', continent: 'europe',
    rarity: 'common', style: 'guardian',
    stats: { hp: 133, power: 16, defence: 14, speed: 65 },
    color: 0x7e9de5, accent: 0xf0a8c3,
    abilityA: { move: 'Glimmer Field', power: 14, color: 0x7e9de5 },
    abilityB: { move: 'Comet Seed', power: 21, color: 0xd77bc2 },
  },
  {
    id: 'europe-mallowisp', name: 'Mallowisp', continent: 'europe',
    rarity: 'common', style: 'trickster',
    stats: { hp: 151, power: 19, defence: 17, speed: 70 },
    color: 0x70c8cc, accent: 0x8cd5b5,
    abilityA: { move: 'Softshell', power: 0, color: 0xd77bc2 },
    abilityB: { move: 'Rattleclap', power: 17, color: 0x70c8cc },
  },
  {
    id: 'europe-cluckoo', name: 'Cluckoo', continent: 'europe',
    rarity: 'common', style: 'hunter',
    stats: { hp: 160, power: 19, defence: 14, speed: 65 },
    color: 0xedc98f, accent: 0xef8f4a,
    abilityA: { move: 'Dash Peck', power: 26, color: 0x70c8cc },
    abilityB: { move: 'Spitcorn', power: 19, color: 0xdb7767 },
  },
  {
    id: 'europe-thistail', name: 'Thistail', continent: 'europe',
    rarity: 'common', style: 'survivor',
    stats: { hp: 148, power: 24, defence: 14, speed: 60 },
    color: 0x54aebb, accent: 0xc4b4f0,
    abilityA: { move: 'Rattleclap', power: 18, color: 0xdb7767 },
    abilityB: { move: 'Bramble Rush', power: 26, color: 0xb9d95b },
  },
  {
    id: 'europe-cloverin', name: 'Cloverin', continent: 'europe',
    rarity: 'common', style: 'hunter',
    stats: { hp: 167, power: 18, defence: 16, speed: 65 },
    color: 0x56c48b, accent: 0xf5a863,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xb9d95b },
    abilityB: { move: 'Glimmer Field', power: 16, color: 0x9c80d9 },
  },
  {
    id: 'north-america-rumblet', name: 'Rumblet', continent: 'north-america',
    rarity: 'common', style: 'trickster',
    stats: { hp: 135, power: 16, defence: 14, speed: 70 },
    color: 0xd77bc2, accent: 0xf6ca58,
    abilityA: { move: 'Softshell', power: 0, color: 0xd77bc2 },
    abilityB: { move: 'Rattleclap', power: 16, color: 0x70c8cc },
  },
  {
    id: 'north-america-pinepup', name: 'Pinepup', continent: 'north-america',
    rarity: 'common', style: 'ambusher',
    stats: { hp: 153, power: 19, defence: 17, speed: 55 },
    color: 0xdb7767, accent: 0xc4b4f0,
    abilityA: { move: 'Comet Seed', power: 22, color: 0x70c8cc },
    abilityB: { move: 'Pulse Pebble', power: 19, color: 0xdb7767 },
  },
  {
    id: 'north-america-cindercub', name: 'Cindercub', continent: 'north-america',
    rarity: 'common', style: 'survivor',
    stats: { hp: 171, power: 21, defence: 11, speed: 60 },
    color: 0x9c80d9, accent: 0xf5a863,
    abilityA: { move: 'Rattleclap', power: 17, color: 0xdb7767 },
    abilityB: { move: 'Bramble Rush', power: 26, color: 0xb9d95b },
  },
  {
    id: 'north-america-mosskunk', name: 'Mosskunk', continent: 'north-america',
    rarity: 'common', style: 'hunter',
    stats: { hp: 150, power: 24, defence: 14, speed: 65 },
    color: 0xec8c53, accent: 0xe3ee9b,
    abilityA: { move: 'Pulse Pebble', power: 20, color: 0xb9d95b },
    abilityB: { move: 'Glimmer Field', power: 16, color: 0x9c80d9 },
  },
  {
    id: 'north-america-rivermint', name: 'Rivermint', continent: 'north-america',
    rarity: 'common', style: 'skirmisher',
    stats: { hp: 169, power: 19, defence: 16, speed: 70 },
    color: 0xefa947, accent: 0xe8d47e,
    abilityA: { move: 'Bramble Rush', power: 27, color: 0x9c80d9 },
    abilityB: { move: 'Softshell', power: 0, color: 0x54aebb },
  },
  {
    id: 'south-america-toucub', name: 'Toucub', continent: 'south-america',
    rarity: 'common', style: 'ambusher',
    stats: { hp: 136, power: 16, defence: 14, speed: 55 },
    color: 0x70c8cc, accent: 0xf5a863,
    abilityA: { move: 'Comet Seed', power: 21, color: 0x70c8cc },
    abilityB: { move: 'Pulse Pebble', power: 18, color: 0xdb7767 },
  },
  {
    id: 'south-america-vineva', name: 'Vineva', continent: 'south-america',
    rarity: 'common', style: 'survivor',
    stats: { hp: 154, power: 19, defence: 17, speed: 60 },
    color: 0xb9d95b, accent: 0xe3ee9b,
    abilityA: { move: 'Rattleclap', power: 17, color: 0xdb7767 },
    abilityB: { move: 'Bramble Rush', power: 25, color: 0xb9d95b },
  },
  {
    id: 'south-america-puddlepod', name: 'Puddlepod', continent: 'south-america',
    rarity: 'common', style: 'hunter',
    stats: { hp: 173, power: 22, defence: 11, speed: 65 },
    color: 0x54aebb, accent: 0xe8d47e,
    abilityA: { move: 'Pulse Pebble', power: 20, color: 0xb9d95b },
    abilityB: { move: 'Glimmer Field', power: 15, color: 0x9c80d9 },
  },
  {
    id: 'south-america-mimibloom', name: 'Mimibloom', continent: 'south-america',
    rarity: 'common', style: 'skirmisher',
    stats: { hp: 151, power: 24, defence: 14, speed: 70 },
    color: 0x56c48b, accent: 0xf5dcad,
    abilityA: { move: 'Bramble Rush', power: 27, color: 0x9c80d9 },
    abilityB: { move: 'Softshell', power: 0, color: 0x54aebb },
  },
  {
    id: 'south-america-canopuff', name: 'Canopuff', continent: 'south-america',
    rarity: 'common', style: 'guardian',
    stats: { hp: 171, power: 19, defence: 17, speed: 55 },
    color: 0x7e9de5, accent: 0xf18c72,
    abilityA: { move: 'Glimmer Field', power: 17, color: 0x54aebb },
    abilityB: { move: 'Comet Seed', power: 24, color: 0xec8c53 },
  },
  {
    id: 'oceania-coraloo', name: 'Coraloo', continent: 'oceania',
    rarity: 'common', style: 'survivor',
    stats: { hp: 138, power: 16, defence: 15, speed: 60 },
    color: 0xdb7767, accent: 0xe8d47e,
    abilityA: { move: 'Rattleclap', power: 16, color: 0xdb7767 },
    abilityB: { move: 'Bramble Rush', power: 24, color: 0xb9d95b },
  },
  {
    id: 'oceania-wavlet', name: 'Wavlet', continent: 'oceania',
    rarity: 'common', style: 'hunter',
    stats: { hp: 156, power: 19, defence: 17, speed: 65 },
    color: 0x9c80d9, accent: 0xf5dcad,
    abilityA: { move: 'Pulse Pebble', power: 19, color: 0xb9d95b },
    abilityB: { move: 'Glimmer Field', power: 15, color: 0x9c80d9 },
  },
  {
    id: 'oceania-salties', name: 'Salties', continent: 'oceania',
    rarity: 'common', style: 'skirmisher',
    stats: { hp: 175, power: 22, defence: 11, speed: 70 },
    color: 0xec8c53, accent: 0xf18c72,
    abilityA: { move: 'Bramble Rush', power: 26, color: 0x9c80d9 },
    abilityB: { move: 'Softshell', power: 0, color: 0x54aebb },
  },
  {
    id: 'oceania-kelpup', name: 'Kelpup', continent: 'oceania',
    rarity: 'common', style: 'guardian',
    stats: { hp: 153, power: 25, defence: 14, speed: 55 },
    color: 0xefa947, accent: 0xe5e6c6,
    abilityA: { move: 'Glimmer Field', power: 16, color: 0x54aebb },
    abilityB: { move: 'Comet Seed', power: 24, color: 0xec8c53 },
  },
  {
    id: 'oceania-pearlisk', name: 'Pearlisk', continent: 'oceania',
    rarity: 'common', style: 'trickster',
    stats: { hp: 172, power: 19, defence: 17, speed: 60 },
    color: 0xd77bc2, accent: 0xf0a8c3,
    abilityA: { move: 'Softshell', power: 0, color: 0xec8c53 },
    abilityB: { move: 'Rattleclap', power: 19, color: 0x56c48b },
  },
  {
    id: 'africa-savabex', name: 'Savabex', continent: 'africa',
    rarity: 'uncommon', style: 'skirmisher',
    stats: { hp: 160, power: 20, defence: 14, speed: 62 },
    color: 0xee8c5e, accent: 0xebe1c0,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xb9d95b },
    abilityB: { move: 'Softshell', power: 0, color: 0xec8c53 },
  },
  {
    id: 'africa-kilimba', name: 'Kilimba', continent: 'africa',
    rarity: 'uncommon', style: 'guardian',
    stats: { hp: 164, power: 21, defence: 15, speed: 65 },
    color: 0x54aebb, accent: 0xedd077,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0x54aebb },
    abilityB: { move: 'Glimmer Field', power: 18, color: 0x70c8cc },
  },
  {
    id: 'africa-acaclaw', name: 'Acaclaw', continent: 'africa',
    rarity: 'rare', style: 'hunter',
    stats: { hp: 178, power: 24, defence: 18, speed: 70 },
    color: 0x56c48b, accent: 0x7e9de5,
    abilityA: { move: 'Comet Seed', power: 24, color: 0xefa947 },
    abilityB: { move: 'Rattleclap', power: 21, color: 0xb9d95b },
  },
  {
    id: 'africa-emberdune', name: 'Emberdune', continent: 'africa',
    rarity: 'rare', style: 'ambusher',
    stats: { hp: 170, power: 22, defence: 16, speed: 73 },
    color: 0x70c8cc, accent: 0xb6d85e,
    abilityA: { move: 'Rattleclap', power: 24, color: 0xdb7767 },
    abilityB: { move: 'Bramble Rush', power: 21, color: 0x54aebb },
  },
  {
    id: 'africa-miragehorn', name: 'Miragehorn', continent: 'africa',
    rarity: 'super-rare', style: 'brawler',
    stats: { hp: 186, power: 26, defence: 19, speed: 68 },
    color: 0xf0a8c3, accent: 0xee8c5e,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0x9c80d9 },
    abilityB: { move: 'Softshell', power: 0, color: 0xefa947 },
  },
  {
    id: 'africa-solmane', name: 'Solmane', continent: 'africa',
    rarity: 'super-rare', style: 'survivor',
    stats: { hp: 190, power: 27, defence: 20, speed: 71 },
    color: 0xedd077, accent: 0x56c48b,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0xec8c53 },
    abilityB: { move: 'Glimmer Field', power: 25, color: 0xdb7767 },
  },
  {
    id: 'africa-zephyrion', name: 'Zephyrion', continent: 'africa',
    rarity: 'legendary', style: 'trickster',
    stats: { hp: 196, power: 28, defence: 21, speed: 80 },
    color: 0x7e9de5, accent: 0x70c8cc,
    abilityA: { move: 'Comet Seed', power: 32, color: 0x70c8cc },
    abilityB: { move: 'Rattleclap', power: 29, color: 0x9c80d9 },
  },
  {
    id: 'africa-goldpride', name: 'Goldpride', continent: 'africa',
    rarity: 'legendary', style: 'guardian',
    stats: { hp: 201, power: 29, defence: 22, speed: 83 },
    color: 0xb6d85e, accent: 0xf0a8c3,
    abilityA: { move: 'Rattleclap', power: 32, color: 0xb9d95b },
    abilityB: { move: 'Bramble Rush', power: 29, color: 0xec8c53 },
  },
  {
    id: 'asia-bambloo', name: 'Bambloo', continent: 'asia',
    rarity: 'uncommon', style: 'trickster',
    stats: { hp: 160, power: 20, defence: 14, speed: 62 },
    color: 0x193c1f, accent: 0xebe1c0,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0x9c80d9 },
    abilityB: { move: 'Softshell', power: 0, color: 0xefa947 },
  },
  {
    id: 'asia-jadefin', name: 'Jadefin', continent: 'asia',
    rarity: 'uncommon', style: 'guardian',
    stats: { hp: 164, power: 21, defence: 15, speed: 65 },
    color: 0x255a31, accent: 0xefaa57,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xec8c53 },
    abilityB: { move: 'Glimmer Field', power: 18, color: 0xdb7767 },
  },
  {
    id: 'asia-koinagi', name: 'Koinagi', continent: 'asia',
    rarity: 'rare', style: 'hunter',
    stats: { hp: 178, power: 24, defence: 18, speed: 70 },
    color: 0x24508c, accent: 0xedd077,
    abilityA: { move: 'Comet Seed', power: 24, color: 0x70c8cc },
    abilityB: { move: 'Rattleclap', power: 21, color: 0x9c80d9 },
  },
  {
    id: 'asia-silkmoth', name: 'Silkmoth', continent: 'asia',
    rarity: 'rare', style: 'ambusher',
    stats: { hp: 170, power: 22, defence: 16, speed: 73 },
    color: 0x9c80d9, accent: 0xe5e6c6,
    abilityA: { move: 'Rattleclap', power: 24, color: 0xb9d95b },
    abilityB: { move: 'Bramble Rush', power: 21, color: 0xec8c53 },
  },
  {
    id: 'asia-draconis', name: 'Draconis', continent: 'asia',
    rarity: 'super-rare', style: 'brawler',
    stats: { hp: 186, power: 26, defence: 19, speed: 68 },
    color: 0x2e67b9, accent: 0xd77bc2,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0x54aebb },
    abilityB: { move: 'Softshell', power: 0, color: 0x70c8cc },
  },
  {
    id: 'asia-lotusveil', name: 'Lotusveil', continent: 'asia',
    rarity: 'super-rare', style: 'survivor',
    stats: { hp: 190, power: 27, defence: 20, speed: 71 },
    color: 0x8cd5b5, accent: 0xee8c5e,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0xefa947 },
    abilityB: { move: 'Glimmer Field', power: 25, color: 0xb9d95b },
  },
  {
    id: 'asia-jadeemperor', name: 'Jadeemperor', continent: 'asia',
    rarity: 'legendary', style: 'guardian',
    stats: { hp: 196, power: 28, defence: 21, speed: 80 },
    color: 0x193b6b, accent: 0xefaa57,
    abilityA: { move: 'Comet Seed', power: 32, color: 0xdb7767 },
    abilityB: { move: 'Rattleclap', power: 29, color: 0x54aebb },
  },
  {
    id: 'asia-qilin', name: 'Qilin', continent: 'asia',
    rarity: 'legendary', style: 'skirmisher',
    stats: { hp: 201, power: 29, defence: 22, speed: 83 },
    color: 0xcf9f6b, accent: 0x9c80d9,
    abilityA: { move: 'Rattleclap', power: 32, color: 0x9c80d9 },
    abilityB: { move: 'Bramble Rush', power: 29, color: 0xefa947 },
  },
  {
    id: 'europe-fernwick', name: 'Fernwick', continent: 'europe',
    rarity: 'uncommon', style: 'survivor',
    stats: { hp: 160, power: 20, defence: 14, speed: 62 },
    color: 0x56c48b, accent: 0xebe1c0,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0x54aebb },
    abilityB: { move: 'Softshell', power: 0, color: 0x70c8cc },
  },
  {
    id: 'europe-mistral', name: 'Mistral', continent: 'europe',
    rarity: 'uncommon', style: 'skirmisher',
    stats: { hp: 164, power: 21, defence: 15, speed: 65 },
    color: 0x70c8cc, accent: 0xc4b4f0,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xefa947 },
    abilityB: { move: 'Glimmer Field', power: 18, color: 0xb9d95b },
  },
  {
    id: 'europe-glaciel', name: 'Glaciel', continent: 'europe',
    rarity: 'rare', style: 'guardian',
    stats: { hp: 178, power: 24, defence: 18, speed: 70 },
    color: 0x54aebb, accent: 0xbfbfb7,
    abilityA: { move: 'Comet Seed', power: 24, color: 0xdb7767 },
    abilityB: { move: 'Rattleclap', power: 21, color: 0x54aebb },
  },
  {
    id: 'europe-thornrose', name: 'Thornrose', continent: 'europe',
    rarity: 'rare', style: 'ambusher',
    stats: { hp: 170, power: 22, defence: 16, speed: 73 },
    color: 0xdb7767, accent: 0x70c8cc,
    abilityA: { move: 'Rattleclap', power: 24, color: 0x9c80d9 },
    abilityB: { move: 'Bramble Rush', power: 21, color: 0xefa947 },
  },
  {
    id: 'europe-aurorix', name: 'Aurorix', continent: 'europe',
    rarity: 'super-rare', style: 'trickster',
    stats: { hp: 186, power: 26, defence: 19, speed: 68 },
    color: 0x8cd5b5, accent: 0x7e9de5,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0xec8c53 },
    abilityB: { move: 'Softshell', power: 0, color: 0xdb7767 },
  },
  {
    id: 'europe-stormquill', name: 'Stormquill', continent: 'europe',
    rarity: 'super-rare', style: 'hunter',
    stats: { hp: 190, power: 27, defence: 20, speed: 71 },
    color: 0xefaa57, accent: 0xedd077,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0x70c8cc },
    abilityB: { move: 'Glimmer Field', power: 25, color: 0x9c80d9 },
  },
  {
    id: 'europe-caliburn', name: 'Caliburn', continent: 'europe',
    rarity: 'legendary', style: 'brawler',
    stats: { hp: 196, power: 28, defence: 21, speed: 80 },
    color: 0xc4b4f0, accent: 0x70c8cc,
    abilityA: { move: 'Comet Seed', power: 32, color: 0xb9d95b },
    abilityB: { move: 'Rattleclap', power: 29, color: 0xec8c53 },
  },
  {
    id: 'europe-grimoire', name: 'Grimoire', continent: 'europe',
    rarity: 'legendary', style: 'guardian',
    stats: { hp: 201, power: 29, defence: 22, speed: 83 },
    color: 0x11120e, accent: 0xf0a8c3,
    abilityA: { move: 'Rattleclap', power: 32, color: 0x54aebb },
    abilityB: { move: 'Bramble Rush', power: 29, color: 0x70c8cc },
  },
  {
    id: 'north-america-copperfox', name: 'Copperfox', continent: 'north-america',
    rarity: 'uncommon', style: 'hunter',
    stats: { hp: 160, power: 20, defence: 14, speed: 62 },
    color: 0xefaa57, accent: 0xebe1c0,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xec8c53 },
    abilityB: { move: 'Softshell', power: 0, color: 0xdb7767 },
  },
  {
    id: 'north-america-conifer', name: 'Conifer', continent: 'north-america',
    rarity: 'uncommon', style: 'survivor',
    stats: { hp: 164, power: 21, defence: 15, speed: 65 },
    color: 0x9c80d9, accent: 0xd5eb9c,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0x70c8cc },
    abilityB: { move: 'Glimmer Field', power: 18, color: 0x9c80d9 },
  },
  {
    id: 'north-america-canyonhowl', name: 'Canyonhowl', continent: 'north-america',
    rarity: 'rare', style: 'skirmisher',
    stats: { hp: 178, power: 24, defence: 18, speed: 70 },
    color: 0xee8c5e, accent: 0xdb7767,
    abilityA: { move: 'Comet Seed', power: 24, color: 0xb9d95b },
    abilityB: { move: 'Rattleclap', power: 21, color: 0xec8c53 },
  },
  {
    id: 'north-america-bisonrock', name: 'Bisonrock', continent: 'north-america',
    rarity: 'rare', style: 'guardian',
    stats: { hp: 170, power: 22, defence: 16, speed: 73 },
    color: 0xedd077, accent: 0x9c80d9,
    abilityA: { move: 'Rattleclap', power: 24, color: 0x54aebb },
    abilityB: { move: 'Bramble Rush', power: 21, color: 0x70c8cc },
  },
  {
    id: 'north-america-thunderclaw', name: 'Thunderclaw', continent: 'north-america',
    rarity: 'super-rare', style: 'brawler',
    stats: { hp: 186, power: 26, defence: 19, speed: 68 },
    color: 0xc4b4f0, accent: 0xee8c5e,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0xefa947 },
    abilityB: { move: 'Softshell', power: 0, color: 0xb9d95b },
  },
  {
    id: 'north-america-geodeheart', name: 'Geodeheart', continent: 'north-america',
    rarity: 'super-rare', style: 'guardian',
    stats: { hp: 190, power: 27, defence: 20, speed: 71 },
    color: 0xdb7767, accent: 0xd5eb9c,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0xdb7767 },
    abilityB: { move: 'Glimmer Field', power: 25, color: 0x54aebb },
  },
  {
    id: 'north-america-colossus', name: 'Colossus', continent: 'north-america',
    rarity: 'legendary', style: 'guardian',
    stats: { hp: 196, power: 28, defence: 21, speed: 80 },
    color: 0xd77bc2, accent: 0xefaa57,
    abilityA: { move: 'Comet Seed', power: 32, color: 0x9c80d9 },
    abilityB: { move: 'Rattleclap', power: 29, color: 0xefa947 },
  },
  {
    id: 'north-america-aurorursa', name: 'Aurorursa', continent: 'north-america',
    rarity: 'legendary', style: 'survivor',
    stats: { hp: 201, power: 29, defence: 22, speed: 83 },
    color: 0x9c80d9, accent: 0xedd077,
    abilityA: { move: 'Rattleclap', power: 32, color: 0xec8c53 },
    abilityB: { move: 'Bramble Rush', power: 29, color: 0xdb7767 },
  },
  {
    id: 'south-america-cacaofin', name: 'Cacaofin', continent: 'south-america',
    rarity: 'uncommon', style: 'trickster',
    stats: { hp: 160, power: 20, defence: 14, speed: 62 },
    color: 0xee8c5e, accent: 0xd5eb9c,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xefa947 },
    abilityB: { move: 'Softshell', power: 0, color: 0xb9d95b },
  },
  {
    id: 'south-america-piranhax', name: 'Piranhax', continent: 'south-america',
    rarity: 'uncommon', style: 'ambusher',
    stats: { hp: 164, power: 21, defence: 15, speed: 65 },
    color: 0x54aebb, accent: 0x56c48b,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xdb7767 },
    abilityB: { move: 'Glimmer Field', power: 18, color: 0x54aebb },
  },
  {
    id: 'south-america-emeraldboa', name: 'Emeraldboa', continent: 'south-america',
    rarity: 'rare', style: 'survivor',
    stats: { hp: 178, power: 24, defence: 18, speed: 70 },
    color: 0x56c48b, accent: 0xb6d85e,
    abilityA: { move: 'Comet Seed', power: 24, color: 0x9c80d9 },
    abilityB: { move: 'Rattleclap', power: 21, color: 0xefa947 },
  },
  {
    id: 'south-america-quetzal', name: 'Quetzal', continent: 'south-america',
    rarity: 'rare', style: 'hunter',
    stats: { hp: 170, power: 22, defence: 16, speed: 73 },
    color: 0x7e9de5, accent: 0xebe1c0,
    abilityA: { move: 'Rattleclap', power: 24, color: 0xec8c53 },
    abilityB: { move: 'Bramble Rush', power: 21, color: 0xdb7767 },
  },
  {
    id: 'south-america-amazonix', name: 'Amazonix', continent: 'south-america',
    rarity: 'super-rare', style: 'brawler',
    stats: { hp: 186, power: 26, defence: 19, speed: 68 },
    color: 0xb6d85e, accent: 0xebe1c0,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0x70c8cc },
    abilityB: { move: 'Softshell', power: 0, color: 0x9c80d9 },
  },
  {
    id: 'south-america-sunpetal', name: 'Sunpetal', continent: 'south-america',
    rarity: 'super-rare', style: 'trickster',
    stats: { hp: 190, power: 27, defence: 20, speed: 71 },
    color: 0xedd077, accent: 0x7e9de5,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0xb9d95b },
    abilityB: { move: 'Glimmer Field', power: 25, color: 0xec8c53 },
  },
  {
    id: 'south-america-rainbowcoatl', name: 'Rainbowcoatl', continent: 'south-america',
    rarity: 'legendary', style: 'skirmisher',
    stats: { hp: 196, power: 28, defence: 21, speed: 80 },
    color: 0x70c8cc, accent: 0xf5dcad,
    abilityA: { move: 'Comet Seed', power: 32, color: 0x54aebb },
    abilityB: { move: 'Rattleclap', power: 29, color: 0x70c8cc },
  },
  {
    id: 'south-america-eldorado', name: 'Eldorado', continent: 'south-america',
    rarity: 'legendary', style: 'guardian',
    stats: { hp: 201, power: 29, defence: 22, speed: 83 },
    color: 0xd5eb9c, accent: 0xee8c5e,
    abilityA: { move: 'Rattleclap', power: 32, color: 0xefa947 },
    abilityB: { move: 'Bramble Rush', power: 29, color: 0xb9d95b },
  },
  {
    id: 'oceania-reefpod', name: 'Reefpod', continent: 'oceania',
    rarity: 'uncommon', style: 'survivor',
    stats: { hp: 160, power: 20, defence: 14, speed: 62 },
    color: 0xee8c5e, accent: 0xf0a8c3,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0x70c8cc },
    abilityB: { move: 'Softshell', power: 0, color: 0x9c80d9 },
  },
  {
    id: 'oceania-tidewisp', name: 'Tidewisp', continent: 'oceania',
    rarity: 'uncommon', style: 'trickster',
    stats: { hp: 164, power: 21, defence: 15, speed: 65 },
    color: 0x9c80d9, accent: 0xefaa57,
    abilityA: { move: 'Pulse Pebble', power: 21, color: 0xb9d95b },
    abilityB: { move: 'Glimmer Field', power: 18, color: 0xec8c53 },
  },
  {
    id: 'oceania-opalpup', name: 'Opalpup', continent: 'oceania',
    rarity: 'rare', style: 'hunter',
    stats: { hp: 178, power: 24, defence: 18, speed: 70 },
    color: 0xd77bc2, accent: 0xe8d47e,
    abilityA: { move: 'Comet Seed', power: 24, color: 0x54aebb },
    abilityB: { move: 'Rattleclap', power: 21, color: 0x70c8cc },
  },
  {
    id: 'oceania-lagoona', name: 'Lagoona', continent: 'oceania',
    rarity: 'rare', style: 'skirmisher',
    stats: { hp: 170, power: 22, defence: 16, speed: 73 },
    color: 0xdb7767, accent: 0xee8c5e,
    abilityA: { move: 'Rattleclap', power: 24, color: 0xefa947 },
    abilityB: { move: 'Bramble Rush', power: 21, color: 0xb9d95b },
  },
  {
    id: 'oceania-barriax', name: 'Barriax', continent: 'oceania',
    rarity: 'super-rare', style: 'guardian',
    stats: { hp: 186, power: 26, defence: 19, speed: 68 },
    color: 0xefaa57, accent: 0x9c80d9,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0xdb7767 },
    abilityB: { move: 'Softshell', power: 0, color: 0x54aebb },
  },
  {
    id: 'oceania-cyclonix', name: 'Cyclonix', continent: 'oceania',
    rarity: 'super-rare', style: 'brawler',
    stats: { hp: 190, power: 27, defence: 20, speed: 71 },
    color: 0xedd077, accent: 0xd77bc2,
    abilityA: { move: 'Pulse Pebble', power: 28, color: 0x9c80d9 },
    abilityB: { move: 'Glimmer Field', power: 25, color: 0xefa947 },
  },
  {
    id: 'oceania-leviathan', name: 'Leviathan', continent: 'oceania',
    rarity: 'legendary', style: 'guardian',
    stats: { hp: 196, power: 28, defence: 21, speed: 80 },
    color: 0x555754, accent: 0xefaa57,
    abilityA: { move: 'Comet Seed', power: 32, color: 0xec8c53 },
    abilityB: { move: 'Rattleclap', power: 29, color: 0xdb7767 },
  },
  {
    id: 'oceania-southernstar', name: 'Southernstar', continent: 'oceania',
    rarity: 'legendary', style: 'survivor',
    stats: { hp: 201, power: 29, defence: 22, speed: 83 },
    color: 0xf0a8c3, accent: 0xdb7767,
    abilityA: { move: 'Rattleclap', power: 32, color: 0x70c8cc },
    abilityB: { move: 'Bramble Rush', power: 29, color: 0x9c80d9 },
  },
];

const buildAbility = (seed: SpeciesSeed, ability: AbilitySeed, suffix: 'a' | 'b'): AbilityDefinition => {
  const move = movesByName[ability.move];
  const set = regions[seed.continent];
  return {
    id: `${seed.id}-${suffix}`,
    name: `${set.element} ${move.name}`,
    element: set.element,
    kind: move.kind,
    delivery: move.delivery,
    effect: move.effect,
    power: ability.power,
    range: move.range,
    radius: move.radius,
    projectileSpeed: move.projectileSpeed,
    energyCost: move.cost,
    cooldown: move.cooldown,
    windup: move.windup,
    recovery: move.recovery,
    requiresLineOfSight: move.requiresLineOfSight,
    color: ability.color,
  };
};

export const roster: CreatureDefinition[] = speciesSeeds.map((seed) => {
  const set = regions[seed.continent];
  return {
    id: seed.id,
    name: seed.name,
    continent: seed.continent,
    region: set.region,
    habitat: set.habitat,
    element: set.element,
    rarity: seed.rarity,
    style: seed.style,
    color: seed.color,
    accent: seed.accent,
    catchDifficulty: catchDifficultyByRarity[seed.rarity],
    stats: { ...seed.stats, energy: 100 },
    abilities: [buildAbility(seed, seed.abilityA, 'a'), buildAbility(seed, seed.abilityB, 'b')],
    lore: `${seed.name} is a ${set.habitat} wanderer that slips between the living world and the data tide.`,
  };
});

function findFallback(): CreatureDefinition {
  const creature = roster.find((entry) => entry.id === FALLBACK_SPECIES_ID);
  if (!creature) throw new Error(`Garaimon: FALLBACK_SPECIES_ID "${FALLBACK_SPECIES_ID}" is not in the roster.`);
  return creature;
}

const fallbackCreature = findFallback();

/**
 * Looks up a creature. An unknown id is a content bug, so it is reported rather
 * than silently swapped; the fallback keeps the current battle playable.
 */
export const getCreature = (id: string): CreatureDefinition => {
  const creature = roster.find((entry) => entry.id === id);
  if (creature) return creature;
  console.error(`Garaimon: no creature is registered with the id "${id}". Showing "${FALLBACK_SPECIES_ID}" instead so the battle can continue, but this id needs fixing.`);
  return fallbackCreature;
};

export const getAbility = (id: string): AbilityDefinition | undefined => roster.flatMap((creature) => creature.abilities).find((ability) => ability.id === id);
export const creaturesOn = (continent: ContinentId): CreatureDefinition[] => roster.filter((creature) => creature.continent === continent);
